import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, History, Loader2, Trash2, Upload } from "lucide-react";

export function FileUpload({ onLoaded }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const pick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/reconcile/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onLoaded(data, file.name);
            toast.success(`Read ${data.rows} positions from ${file.name}`, { description: `Columns used: ${data.columns.ticker} / ${data.columns.quantity}` });
        } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
    };
    return (
        <>
            <input ref={ref} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" className="hidden" onChange={pick} data-testid="reconcile-file-input" />
            <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid="reconcile-upload-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}Upload broker file
            </Button>
        </>
    );
}

const when = (iso) => new Date(iso).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function HistoryRow({ h, onDelete }) {
    return (
        <tr className="border-t border-border/50" data-testid={`reconcile-history-row-${h.id}`}>
            <td className="py-2 pr-3 num whitespace-nowrap">{when(h.created_at)}</td>
            <td className="pr-3 text-xs text-muted-foreground truncate max-w-[180px]">{h.source}</td>
            <td className="pr-3 text-right num">{h.broker_positions}</td>
            <td className="pr-3 text-right num text-gain">{h.match}</td>
            <td className={`pr-3 text-right num ${h.mismatch ? "text-loss" : ""}`}>{h.mismatch}</td>
            <td className={`pr-3 text-right num ${h.not_in_app ? "text-loss" : ""}`}>{h.not_in_app}</td>
            <td className="pr-3 text-right num">{h.not_in_broker}</td>
            <td className="pr-3">{h.all_matched && <span className="inline-flex items-center gap-1 text-xs text-gain"><CheckCircle2 className="w-3.5 h-3.5" />All matched</span>}</td>
            <td className="text-right">
                <button onClick={() => window.confirm("Delete this saved reconciliation?") && onDelete(h.id)} title="Delete this entry" aria-label="Delete this entry"
                    className="text-muted-foreground hover:text-loss" data-testid={`reconcile-history-delete-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </td>
        </tr>
    );
}

export function HistoryCard() {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["reconcile-history"], queryFn: async () => (await api.get("/reconcile/history")).data });
    const items = data?.items || [];
    const del = async (id) => {
        try { await api.delete(`/reconcile/history/${id}`); qc.invalidateQueries({ queryKey: ["reconcile-history"] }); }
        catch (e) { toast.error(apiErr(e)); }
    };
    return (
        <Card className="p-4 space-y-3" data-testid="reconcile-history">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="font-heading text-base font-semibold inline-flex items-center gap-2"><History className="w-4 h-4" />Reconciliation history</h2>
                <span className="text-xs text-muted-foreground" data-testid="reconcile-last-match">
                    {data?.last_full_match ? `Last full match: ${when(data.last_full_match)}` : "No full match yet"}
                </span>
            </div>
            {items.length ? (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-muted-foreground text-[10px] uppercase tracking-wider">
                            <tr><th className="text-left py-1">When</th><th className="text-left">Source</th><th className="text-right pr-3">Broker lines</th><th className="text-right pr-3">Match</th><th className="text-right pr-3">Different</th><th className="text-right pr-3">Missing in app</th><th className="text-right pr-3">Not at broker</th><th></th><th></th></tr>
                        </thead>
                        <tbody>{items.map((h) => <HistoryRow key={h.id} h={h} onDelete={del} />)}</tbody>
                    </table>
                </div>
            ) : <p className="text-xs text-muted-foreground" data-testid="reconcile-history-empty">Each comparison you run is saved here as a summary.</p>}
        </Card>
    );
}
